import React, { useState, useEffect } from 'react';
import { X, Link2 } from 'lucide-react';
import { usePublishingStore } from '../../store/publishingStore';
import { toast } from 'sonner';
import { getPlatformIcon, PlatformConfig } from './components/PlatformConnectionCard';

interface ConnectPlatformModalProps {
    isOpen: boolean;
    onClose: () => void;
    platformConfig: PlatformConfig | null;
}

const ConnectPlatformModal: React.FC<ConnectPlatformModalProps> = ({
    isOpen,
    onClose,
    platformConfig,
}) => {
    const { connectPlatform, fetchPlatforms } = usePublishingStore();

    const [accountName, setAccountName] = useState('');
    const [accountId, setAccountId] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setAccountName('');
            setAccountId('');
        }
    }, [isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!platformConfig) return;

        if (!accountName.trim()) {
            toast.error('Please enter your account username/handle');
            return;
        }

        setIsSubmitting(true);

        try {
            const data: { platform: string; accountName: string; accountId?: string } = {
                platform: platformConfig.platform,
                accountName: accountName.trim().replace('@', ''),
            };
            if (accountId.trim()) {
                data.accountId = accountId.trim();
            }
            await connectPlatform(data);

            await fetchPlatforms();
            onClose();
        } catch (error) {
            console.error('Failed to connect platform:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen || !platformConfig) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-80" onClick={onClose} />

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

                <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-3xl">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 ${platformConfig.color} rounded-xl flex items-center justify-center text-white`}>
                                {getPlatformIcon(platformConfig.platform, 'w-6 h-6')}
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Connect {platformConfig.name}
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Link your account to start publishing
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Info Banner */}
                    <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                            <strong>Note:</strong> This is a mock connection for demonstration. In production,
                            this would redirect to {platformConfig.name}'s OAuth page.
                        </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Account Username */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                Account Username/Handle *
                            </label>
                            <div className="relative">
                                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">@</span>
                                <input
                                    type="text"
                                    value={accountName}
                                    onChange={(e) => setAccountName(e.target.value)}
                                    placeholder="yourusername"
                                    className="w-full pl-8 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                                />
                            </div>
                        </div>

                        {/* Account ID (optional) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                Channel/Page ID (Optional)
                            </label>
                            <input
                                type="text"
                                value={accountId}
                                onChange={(e) => setAccountId(e.target.value)}
                                placeholder="e.g., UC1234567890"
                                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                            />
                            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                                For YouTube channels, pages, or specific accounts
                            </p>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 px-4 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-indigo-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Link2 className="w-4 h-4" />
                                {isSubmitting ? 'Connecting...' : 'Connect Account'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ConnectPlatformModal;
